import React, { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, CheckCircle2, ExternalLink, Image as ImageIcon,
  Plus, Trash2, ArrowUp, ArrowDown, MousePointerClick, Grid3x3, Sparkles,
} from "lucide-react";
import { Header } from "../components/Header";
import MediaPicker from "../components/MediaPicker";
import { api, formatApiErrorDetail, uploadImage, resolveAsset } from "../lib/api";

// Preset page_keys users can pick. Custom pages are appended dynamically at runtime.
const PRESET_KEYS = [
  { key: "home", label: "Home page", preview: "/" },
  { key: "retro", label: "Retro (/blast-from-the-past)", preview: "/blast-from-the-past" },
  { key: "news", label: "News page (/news)", preview: "/news" },
  { key: "videos", label: "Videos page (/videos)", preview: "/videos" },
  { key: "stats", label: "Stats page (/stats)", preview: "/stats" },
  { key: "spotify", label: "Spotify page (/spotify)", preview: "/spotify" },
  { key: "social", label: "#PUSB Social (/social)", preview: "/social" },
  { key: "contact", label: "Contact page (/contact)", preview: "/contact" },
];

export default function AdminPageTiles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = searchParams.get("page_key") || "home";

  const [tiles, setTiles] = useState(null);
  const [customPages, setCustomPages] = useState([]);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/page-tiles", { params: { page_key: pageKey } });
      setTiles(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  useEffect(() => {
    api.get("/admin/custom-pages").then((r) => setCustomPages(r.data || [])).catch(() => {});
  }, []);

  const allKeys = [
    ...PRESET_KEYS,
    ...customPages.map((p) => ({
      key: `page:${p.slug}`,
      label: `Custom: ${p.title} (/page/${p.slug})`,
      preview: `/page/${p.slug}`,
    })),
  ];

  const currentKey = allKeys.find((k) => k.key === pageKey);
  const previewPath = currentKey?.preview || "/";

  const addTile = async () => {
    setError("");
    try {
      await api.post("/admin/page-tiles", {
        page_key: pageKey,
        title: "New tile",
        subtitle: "",
        tile_color: "#002d75",
        link_type: "external",
        external_url: "",
      });
      refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]" data-testid="page-admin-page-tiles">
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <Grid3x3 size={16} strokeWidth={2.5} />
            <span className="label-kicker">Universal tiles</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Tiles on any page.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            Pick a page from the dropdown, then add, edit, reorder or delete as many tiles as you want.
            Tiles render in a 1&nbsp;/&nbsp;2&nbsp;/&nbsp;3-column responsive grid below that page's existing content.
          </p>
        </div>

        {/* Page picker */}
        <div className="mb-8 rounded-md border border-black/10 bg-[#F8FAFC] p-5">
          <label className="label-kicker text-[var(--brand-body)]" htmlFor="page-key-select">
            Which page
          </label>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              id="page-key-select"
              data-testid="page-key-select"
              value={pageKey}
              onChange={(e) => setSearchParams({ page_key: e.target.value })}
              className="rounded-md border border-black/10 bg-white px-3 py-2 font-display text-sm font-semibold"
            >
              {allKeys.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label}
                </option>
              ))}
            </select>
            {previewPath ? (
              <a
                href={previewPath}
                target="_blank"
                rel="noreferrer"
                data-testid="page-key-preview"
                className="inline-flex items-center gap-1.5 text-sm text-brand-accent hover:underline"
              >
                Preview <ExternalLink size={14} />
              </a>
            ) : null}
            <div className="ml-auto flex items-center gap-3">
              <span className="text-sm text-[var(--brand-body)]">
                {tiles?.length ?? 0} tile{(tiles?.length ?? 0) === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                onClick={addTile}
                data-testid="add-page-tile"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent"
              >
                <Plus size={14} /> Add tile
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-[var(--brand-body)]">
            page_key = <code>{pageKey}</code>
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-page-tiles-error"
          >
            {error}
          </p>
        ) : null}

        {tiles === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : tiles.length === 0 ? (
          <div className="rounded-md border border-dashed border-black/15 p-10 text-center">
            <Sparkles size={22} className="mx-auto text-brand-accent" />
            <p className="mt-3 font-display text-lg font-bold">No tiles on this page yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">Click "Add tile" above to create one.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-page-tiles-list">
            {tiles.map((t, i) => (
              <TileEditor
                key={t.id}
                tile={t}
                index={i}
                total={tiles.length}
                onSaved={refresh}
                setOuterError={setError}
                customPages={customPages}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function TileEditor({ tile, index, total, onSaved, setOuterError, customPages }) {
  const [form, setForm] = useState({
    title: tile.title || "",
    subtitle: tile.subtitle || "",
    image_url: tile.image_url || "",
    tile_color: tile.tile_color || "#002d75",
    link_type: tile.link_type || "external",
    external_url: tile.external_url || "",
    custom_page_slug: tile.custom_page_slug || "",
    internal_path: tile.internal_path || "",
    cta_label: tile.cta_label || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setLocalErr("");
    setSaving(true);
    setSaved(false);
    try {
      await api.put(`/admin/page-tiles/${tile.id}`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setLocalErr(msg);
      setOuterError(msg);
    } finally {
      setSaving(false);
    }
  };

  const onImage = async (file) => {
    if (!file) return;
    setLocalErr("");
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setField("image_url")(res.url);
    } catch (e) {
      setLocalErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this tile?")) return;
    try {
      await api.delete(`/admin/page-tiles/${tile.id}`);
      onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (direction) => {
    try {
      await api.post(`/admin/page-tiles/${tile.id}/move`, { direction });
      onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const linkValid =
    (form.link_type === "external" && form.external_url.trim()) ||
    (form.link_type === "internal" && form.internal_path.trim()) ||
    (form.link_type === "custom_page" && form.custom_page_slug.trim());

  return (
    <article
      className="rounded-md border border-black/10 bg-white p-6"
      data-testid={`page-tile-editor-${tile.id}`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-sm text-white"
            style={{ backgroundColor: form.tile_color }}
          >
            <Sparkles size={18} />
          </div>
          <div>
            <p className="label-kicker text-[var(--brand-body)]">
              Tile {index + 1} of {total}
            </p>
            <p className="font-display text-lg font-bold leading-tight">
              {form.title || "Untitled"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => move("up")}
            disabled={index === 0}
            data-testid={`tile-move-up-${tile.id}`}
            className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Move up"
          >
            <ArrowUp size={14} />
          </button>
          <button
            type="button"
            onClick={() => move("down")}
            disabled={index === total - 1}
            data-testid={`tile-move-down-${tile.id}`}
            className="rounded-md border border-black/10 px-2 py-1.5 text-sm hover:border-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Move down"
          >
            <ArrowDown size={14} />
          </button>
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-md border border-black/10 bg-[#F8FAFC] px-2 py-1 text-xs text-[var(--brand-body)]"
            data-testid={`tile-clicks-${tile.id}`}
          >
            <MousePointerClick size={12} /> {tile.clicks || 0}
          </span>
          <button
            type="button"
            onClick={remove}
            data-testid={`tile-delete-${tile.id}`}
            className="ml-1 rounded-md border border-[#E63946]/40 px-2 py-1.5 text-sm text-[#E63946] hover:bg-[#E63946] hover:text-white"
            aria-label="Delete tile"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Title">
          <input
            data-testid={`tile-title-${tile.id}`}
            value={form.title}
            maxLength={160}
            onChange={(e) => setField("title")(e.target.value)}
            className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Subtitle">
          <input
            data-testid={`tile-subtitle-${tile.id}`}
            value={form.subtitle}
            maxLength={300}
            onChange={(e) => setField("subtitle")(e.target.value)}
            className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Bottom-left button label (default: 'Open' or 'Open link')">
          <input
            data-testid={`tile-cta-${tile.id}`}
            value={form.cta_label}
            maxLength={40}
            placeholder="Open"
            onChange={(e) => setField("cta_label")(e.target.value)}
            className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Background colour">
          <div className="flex items-center gap-2">
            <input
              type="color"
              data-testid={`tile-color-${tile.id}`}
              value={form.tile_color}
              onChange={(e) => setField("tile_color")(e.target.value)}
              className="h-9 w-12 rounded-md border border-black/10"
            />
            <input
              value={form.tile_color}
              onChange={(e) => setField("tile_color")(e.target.value)}
              className="w-full rounded-md border border-black/10 px-3 py-2 text-sm font-mono"
            />
          </div>
        </Field>
        <Field label="Background image">
          <div className="flex items-center gap-2">
            <label
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-black/10 px-3 py-2 text-sm hover:border-[#0A0F17]"
              data-testid={`tile-upload-${tile.id}`}
            >
              <ImageIcon size={14} />
              {uploading ? "Uploading…" : form.image_url ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onImage(e.target.files?.[0])}
              />
            </label>

            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid={`tile-library-${tile.id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-2 text-sm hover:border-[#0A0F17]"
            >
              <ImageIcon size={14} /> Pick from library
            </button>

            {form.image_url ? (
              <>
                <img
                  src={resolveAsset(form.image_url)}
                  alt=""
                  className="h-9 w-9 rounded-md border border-black/10 object-cover"
                />
                <button
                  type="button"
                  onClick={() => setField("image_url")("")}
                  className="text-xs text-[var(--brand-body)] underline"
                >
                  Remove
                </button>
              </>
            ) : null}

            {pickerOpen ? (
              <MediaPicker
                open={pickerOpen}
                onSelect={(url) => {
                  setField("image_url")(url);
                  setPickerOpen(false);
                }}
                onClose={() => setPickerOpen(false)}
              />
            ) : null}
          </div>
        </Field>
      </div>

      {/* Link destination */}
      <div className="mt-5 rounded-md border border-black/10 bg-[#F8FAFC] p-4">
        <p className="label-kicker text-[var(--brand-body)]">Where does this tile go?</p>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`tile-link-type-${tile.id}`}>
          {[
            { v: "external", label: "External URL" },
            { v: "custom_page", label: "Custom page" },
            { v: "internal", label: "Internal path" },
          ].map((opt) => (
            <label
              key={opt.v}
              className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                form.link_type === opt.v
                  ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                  : "border-black/10 bg-white"
              }`}
            >
              <input
                type="radio"
                name={`lt-${tile.id}`}
                value={opt.v}
                checked={form.link_type === opt.v}
                onChange={() => setField("link_type")(opt.v)}
                className="hidden"
              />
              {opt.label}
            </label>
          ))}
        </div>

        {form.link_type === "external" ? (
          <Field label="URL" className="mt-3">
            <input
              data-testid={`tile-external-${tile.id}`}
              value={form.external_url}
              placeholder="https://example.com"
              onChange={(e) => setField("external_url")(e.target.value)}
              className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
            />
          </Field>
        ) : null}

        {form.link_type === "custom_page" ? (
          <Field label="Custom page" className="mt-3">
            <select
              data-testid={`tile-custom-page-${tile.id}`}
              value={form.custom_page_slug}
              onChange={(e) => setField("custom_page_slug")(e.target.value)}
              className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
            >
              <option value="">— pick a page —</option>
              {customPages.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.title} (/page/{p.slug})
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {form.link_type === "internal" ? (
          <Field label="Internal path" className="mt-3">
            <input
              data-testid={`tile-internal-${tile.id}`}
              value={form.internal_path}
              placeholder="/news or /page/about"
              onChange={(e) => setField("internal_path")(e.target.value)}
              className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
            />
          </Field>
        ) : null}
      </div>

      {/* Explore-tile rotation opt-in now lives on its own unified admin
          screen (alongside nav items, so checkboxes + images for the
          home rotation aren't split across two separate pages). */}
      <p
        className="mt-5 rounded-md border border-dashed border-[#6CABDD]/60 bg-[#6CABDD]/5 px-3 py-2 text-sm text-[var(--brand-body)]"
        data-testid={`tile-explore-pickable-row-${tile.id}`}
      >
        Whether this tile appears in the home Explore rotation (and its image there) is
        now managed on{" "}
        <RouterLink to="/admin/explore-rotation" className="font-semibold text-[var(--brand-ink)] underline">
          Home Explore rotation →
        </RouterLink>
        , alongside nav menu items.
      </p>

      {localErr ? (
        <p className="mt-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm">
          {localErr}
        </p>
      ) : null}

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving || !linkValid || !form.title.trim()}
          data-testid={`tile-save-${tile.id}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-accent px-4 py-2 font-display text-sm font-semibold text-white hover:bg-[#0A0F17] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save tile
        </button>
        {saved ? (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
            <CheckCircle2 size={14} /> Saved
          </span>
        ) : null}
      </div>
    </article>
  );
}

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="label-kicker text-[var(--brand-body)]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
